import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types/api';
import { getSecureToken, clearSecureToken } from '../utils/secureStorage';
import { message } from 'antd'; // 全局消息提示，用于展示“访问权限已过期”提示
import { useAuthStore } from '../stores/authStore'; // 引入全局认证 store，用于统一登出
import i18n from '../locales/i18n'; // 引入 i18n 实例用于国际化提示

// Create axios instance with base configuration
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: '/api/v1',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // 防抖标记：避免并发多个请求同时出现 401 时重复提示/重复跳转
  let isHandlingAuthExpiry = false;

  // 统一处理鉴权过期：提示 + 自动登出 + 可选跳转登录页
  const handleAuthExpired = (config?: { suppressRedirect?: boolean; suppressToast?: boolean; suppressLogout?: boolean; reason?: string }) => {
    if (isHandlingAuthExpiry) return; // 已经处理过则直接返回，避免重复
    isHandlingAuthExpiry = true;

    // 1) 可选登出：避免登录页 401（用户名/密码错误）触发无意义登出与状态抖动
    const suppressLogout = config?.suppressLogout === true;
    if (!suppressLogout) {
      try {
        useAuthStore.getState().logout();
      } catch (e) {
        // 兜底：若 store 不可用也确保清理安全存储中的 token
        clearSecureToken();
      }
      try {
        // 立即移除持久化的认证状态，避免刷新后重新读取旧的 isAuthenticated=true
        localStorage.removeItem('auth-storage');
        // 再次尝试深度清理，确保无残留
        (useAuthStore as any).persist?.clearStorage?.();
      } catch (e) { /* noop */ }
    }

    // 2) 可选提示：登录失败场景下不显示“访问权限已过期”
    const suppressToast = config?.suppressToast === true;
    if (!suppressToast) {
      message.error(
        i18n.t('auth:errors.sessionExpired', {
          defaultValue: '访问权限已过期，需要重新登录'
        })
      );
    }

    // 3) 可选跳转登录页（允许调用方通过 suppressAuthRedirect 抑制自动跳转）
    const suppressRedirect = config?.suppressRedirect === true;
    if (!suppressRedirect) {
      // 略微延迟，确保提示可见，并附带 reason=expired 供登录页展示说明
      setTimeout(() => {
        const url = new URL(window.location.origin + '/login');
        url.searchParams.set('reason', 'expired');
        window.location.href = url.toString();
      }, 800);
    }

    // 4) 短暂复位标记，允许后续正常提示（防止永久阻塞）
    setTimeout(() => {
      isHandlingAuthExpiry = false;
    }, 2000);
  };

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      // 从安全存储中读取解密后的 token 并自动附加到请求头
      const token = getSecureToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle common errors
  client.interceptors.response.use(
    (response) => {
      // 兼容应用层返回 { base: { code: 401, message: "Token is expired" } } 的场景
      const data = response?.data as any;
      const base = data?.base;
      // 判断：code 为 401 或 message 包含 Token is expired（大小写不敏感）
      const isAppLevel401 = base && (
        base.code === 401 ||
        (typeof base.message === 'string' && /token is expired/i.test(base.message))
      );

      if (isAppLevel401) {
        // 处理过期：提示 + 登出 + 可选跳转
        const cfg = response.config as any;
        const suppressRedirect = cfg?.suppressAuthRedirect === true;
        const suppressToast = cfg?.suppressAuthExpiredToast === true;
        const suppressLogout = cfg?.suppressLogoutOn401 === true;
        handleAuthExpired({ suppressRedirect, suppressToast, suppressLogout, reason: 'base_401' });

        // 将该响应视为失败，抛出错误给调用方
        const err = new Error(base?.message || 'Unauthorized');
        (err as any).response = response;
        return Promise.reject(err);
      }

      return response;
    },
    (error) => {
      // HTTP 层 401：统一处理鉴权过期
      if (error.response?.status === 401) {
        const cfg = error.config as any;
        const suppressRedirect = cfg?.suppressAuthRedirect === true;
        const suppressToast = cfg?.suppressAuthExpiredToast === true;
        const suppressLogout = cfg?.suppressLogoutOn401 === true;
        handleAuthExpired({ suppressRedirect, suppressToast, suppressLogout, reason: 'http_401' });
        // 覆盖错误消息为本地化文案，避免出现英文的默认提示
        error.message = i18n.t('common:messages.unauthorized', {
          defaultValue: '未授权访问'
        });
      } else if (!error.response) {
        const cfg = error.config as any;
        const logoutOnNetworkError = cfg?.logoutOnNetworkError === true;
        if (logoutOnNetworkError) {
          handleAuthExpired({ suppressToast: true, reason: 'network_unreachable' });
        } else {
          error.message = i18n.t('common:messages.networkError', {
            defaultValue: '网络连接错误，请重试'
          });
        }
      } else {
        const cfg = error.config as any;
        const logoutOnNetworkError = cfg?.logoutOnNetworkError === true;
        const status = error.response.status;
        // 服务不可达场景（如代理返回 502/503/504）：按网络不可达处理
        if (logoutOnNetworkError && (status === 502 || status === 503 || status === 504)) {
          handleAuthExpired({ suppressToast: true, reason: 'server_unreachable' });
          return Promise.reject(error);
        }
        // 5xx 服务器错误：统一使用本地化文案
        if (status >= 500) {
          // 服务器错误：若后端提供 message，拼接显示 reason：{message}
          const backendMsg = (error.response?.data?.base?.message ?? error.response?.data?.message ?? '').toString().trim();
          const prefix = i18n.t('common:messages.serverError', {
            defaultValue: '服务器开了个小差，请稍后重试'
          });
          error.message = backendMsg ? `${prefix}，reason：${backendMsg}` : prefix;
        } else if (status === 403) {
          error.message = i18n.t('common:messages.forbidden', {
            defaultValue: '访问被禁止'
          });
        } else if (status === 404) {
          // 若需要可扩展 notFound 键；这里使用通用错误作为兜底
          error.message = i18n.t('common:messages.error', {
            defaultValue: '发生错误'
          });
        } else if (status >= 400) {
          // 其它 4xx 使用通用错误作为兜底
          error.message = i18n.t('common:messages.error', {
            defaultValue: '发生错误'
          });
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createApiClient();

// Generic API methods
export const api = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.get(url, config);
    return response.data;
  },

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.post(url, data, config);
    return response.data;
  },

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.put(url, data, config);
    return response.data;
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.delete(url, config);
    return response.data;
  },

  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.patch(url, data, config);
    return response.data;
  },
};
